/* eslint-disable max-len */
/* eslint-disable guard-for-in */

'use-strict';

import { IScope } from 'angular';

import { SettingsAPIService } from '../../../shared/api/settings-api.service';
import { AppConstants } from '../../../src/app/@shared-types/config';
import { ConfigService } from '../../../shared/config/config.service';
import { EConstantType } from '../../../src/app/@shared-types/config/enums';
import { EVehicleUsage } from '../../../src/app/@shared-types/vehicle/enums';
import { InterfaceService } from '../../../shared/interface/interface.service';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { TransferCosts } from '../../../src/app/@shared-types/vehicle/transfer-costs';
import { UsersService } from '../../../shared/users/users.service';
import LogService from '../../../src/app/shared/services/log';
import ObjectService from '../../../src/app/shared/services/object';

interface ScopeTransferCosts extends Omit<TransferCosts, 'transfer_cost'> {
  transfer_cost: string,
}

interface ConfigScope extends IScope {
  usageTypes: { [key in EVehicleUsage]: string },
  constants: Partial<AppConstants>,
  transferCosts: ScopeTransferCosts[],
  subsidiaries: string[],
  userFilter: { [key: string]: string },
  locations: {
    key: string,
    value: string,
    subsidiary: string,
  }[],
  bankInformation: {
    key: string,
    value: string,
  }[],
  leadRecipients: { subsidiaryId: string, users: number[] }[],
  filetypes: string[],
  financingExample: {
    upfrontPercentage: string,
    lastPaymentPercentage: string,
    numberMonth: string,
    interestRate: string,
  },
  vehicleStates: { key: string, value: string, external: boolean, usageType: EVehicleUsage }[],
  transmitting: boolean,
  currentConfigPage: string,
  ready: boolean,
  addElementToArray: (array: unknown[] | string, elem: unknown) => void,
  moveObjectInArray: (array: unknown[], index: number, newPostition: number) => void,
  removeObjectFromArray: (index: number, array: unknown[]) => void,
  updateBankInformation: () => Promise<void>,
  updateFiletypes: () => Promise<void>,
  updateFincancingData: () => Promise<void>,
  updateLeadRecipients: () => Promise<void>,
  updateLocations: () => Promise<void>,
  updateMisc: () => Promise<void>,
  updateOnlineDescription: () => Promise<void>,
  updateTransferCosts: () => Promise<void>,
  updateVehicleStates: () => Promise<void>,
}

/* @ngInject */
export default function SalesConstantsController(
  $scope: ConfigScope,
  toastService: ToastService,
  settingsApiService: SettingsAPIService,
  interfaceService: InterfaceService,
  configService: ConfigService,
  objectService: ObjectService,
  logService: LogService,
  numberConversionService: NumberConversionService,
  usersService: UsersService,
): void {
  $scope.constants = {};
  $scope.transferCosts = [];
  $scope.usageTypes = {
    N: 'Neufahrzeug',
    D: 'Vorführfahrzeug',
    S: 'Tageszulassung',
    U: 'Gebrauchtfahrzeug',
    E: 'Dienstfahrzeug',
    R: 'Vermietfahrzeug',
  };
  $scope.userFilter = {};
  $scope.subsidiaries = [];
  $scope.locations = [];
  $scope.bankInformation = [];
  $scope.leadRecipients = [];
  $scope.vehicleStates = [];
  $scope.filetypes = [];
  $scope.transmitting = false;
  $scope.currentConfigPage = 'vehicleStates';
  $scope.financingExample = {
    interestRate: '', lastPaymentPercentage: '', numberMonth: '', upfrontPercentage: '',
  };
  $scope.ready = false;

  usersService.getUsers()
    .forEach((u) => {
      $scope.userFilter[`${u.id}`] = `${u.first_name} ${u.last_name}`;
    });
  /**
   * The API server returns the constant values as saved in the database.
   * Before displaying them convert float numbers to a different decimal delimiter.
   * Save the constants in a scope variable.
   */
  const getAllConstants = async (): Promise<void> => {
    const constants = await settingsApiService.getAllConstants();
    constants.forEach((c) => {
      if (c.attribute === EConstantType.AVAILABILITY_NOTIFICATIONS && c.value) {
        c.value = (c.value as number[]).map((u) => `${u}`).join(', ');
      }

      const value = c.datatype === 'Float' ? String(c.value).replace('.', ',') : c.value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ($scope.constants as any)[c.attribute] = value as undefined;
    });

    $scope.ready = true;
  };

  const leadRecipientsInit = () => {
    const leadRecipients = $scope.constants[EConstantType.LEAD_RECEIPENTS];
    if (!leadRecipients) throw new Error('Fehler beim Laden der Lead-Empfänger');

    leadRecipients.forEach((r) => {
      r.users = r.users.join(', ') as unknown as number[];
    });
    $scope.leadRecipients = leadRecipients;
  };

  /**
   * Locations are saved as key value pairs. To make them editable in the view,
   * convert them to objects.
   */
  const locationConstantsInit = () => {
    const locations = $scope.constants[EConstantType.LOCATIONS];
    const subsidiaries = configService.get('subsidiaries');
    const subsidiaryAssignments = $scope.constants[EConstantType.LOCATION_SUBSIDIARY_ASSIGNMENT];
    if (!$scope.constants[EConstantType.LOCATIONS] || !subsidiaries || !subsidiaryAssignments) throw new Error('Fehler beim Laden der Standorte');

    for (const key in locations) {
      const subsidiaryAssignment = subsidiaryAssignments.find((s) => s.location === key);
      // if (subsidiaryAssignment) location.subsidiary = subsidiaryAssignment.subsidiaryId;

      const locationId = key.replace(`${subsidiaryAssignment?.subsidiaryId}-`, '');

      const location = {
        key: locationId,
        value: locations[key],
        subsidiary: subsidiaryAssignment?.subsidiaryId || '',
      };

      $scope.locations.push(location);
    }

    $scope.subsidiaries = subsidiaries.map((s) => s.id);
  };

  const bankInformationInit = () => {
    const banks = $scope.constants[EConstantType.BANK_INFORMATION];

    for (const key in banks) {
      const bank = {
        key: banks[key],
        value: key,
      };

      $scope.bankInformation.push(bank);
    }
  };

  /** VehicleStates are saved as key value pairs and need to be converted.
   * Also combine the constants with the external vehicle states. */
  const vehicleStateConstantsInit = () => {
    const vehicleStates = $scope.constants[EConstantType.VEHICLE_TYPES];
    const externalVehicleStates = $scope.constants[EConstantType.VEHICLE_TYPES_EXTERNAL_USERS];
    const vehicleUsageTypes = $scope.constants[EConstantType.VEHICLE_STATE_USAGE_TYPE];

    if (!vehicleStates || !externalVehicleStates || !vehicleUsageTypes) throw new Error('Fehler beim Laden der Fahrzeugarten');

    objectService.convertKeyValueObjectToArray<string>(vehicleStates).forEach((vehicleState) => {
      const external = externalVehicleStates.some((state) => vehicleState.key === state);
      const usageType = vehicleUsageTypes.find((u) => u.vehicleState === vehicleState.key);

      $scope.vehicleStates.push({
        key: vehicleState.key,
        value: vehicleState.value,
        external,
        usageType: usageType?.usageType || EVehicleUsage.NEW,
      });
    });
  };

  /** Ask the API server for the transfer costs.
   * Convert the float values to decimal commas. */
  const transferCostsConstantsInit = async (): Promise<void> => {
    const transferCosts = await settingsApiService.getAllTransferCosts();
    transferCosts.forEach((t) => {
      $scope.transferCosts.push({
        id: t.id,
        transfer_cost: numberConversionService.floatToLocalizedString(t.transfer_cost),
        transfer_make: t.transfer_make,
        transfer_model: t.transfer_model,
      });
    });
  };

  const filetypesInit = (): void => {
    const filetypes = $scope.constants[EConstantType.FILETYPES];
    if (!filetypes) throw new Error('Fehler beim Laden der Dokumententypen');

    $scope.filetypes = filetypes;
  };

  const financingExampleInit = (): void => {
    const interestRate = $scope.constants[EConstantType.FINANCING_EXAMPLE_INTEREST_RATE];
    // eslint-disable-next-line max-len
    const lastPaymentPercentage = $scope.constants[EConstantType.FINANCING_EXAMPLE_PERCENTAGE_LAST_PAYMENT];
    const numberMonth = $scope.constants[EConstantType.FINANCING_EXAMPLE_MONTHS];
    const upfrontPercentage = $scope.constants[EConstantType.FINANCING_EXAMPLE_PERCENTAGE_UPFRONT];

    $scope.financingExample = {
      interestRate: interestRate ? numberConversionService.floatToLocalizedString(interestRate) : '0',
      lastPaymentPercentage: String(lastPaymentPercentage) || '0',
      numberMonth: String(numberMonth) || '0',
      upfrontPercentage: upfrontPercentage ? numberConversionService.floatToLocalizedString(upfrontPercentage) : '0',
    };
  };

  /**
   * Ask the API server for all available constants.
   * Prepare the data for editing in the view.
   */
  const prepareConstantsForView = async () => {
    await getAllConstants();

    vehicleStateConstantsInit();
    locationConstantsInit();
    bankInformationInit();
    filetypesInit();
    financingExampleInit();
    leadRecipientsInit();

    await transferCostsConstantsInit();
    interfaceService.applyScope($scope);
  };

  const beginApiTransmission = () => {
    $scope.transmitting = true;
  };

  const endApiTransmission = () => {
    $scope.transmitting = false;
  };

  $scope.updateVehicleStates = async (): Promise<void> => {
    beginApiTransmission();
    const vehicleStates: { [key: string]: string } = {};
    const externalVehicleStates: string[] = [];
    const vehicleUsageTypes: { vehicleState: string, usageType: EVehicleUsage }[] = [];

    $scope.vehicleStates.forEach((s) => {
      vehicleStates[s.key] = s.value;
      vehicleUsageTypes.push({ usageType: s.usageType, vehicleState: s.key });
      if (s.external) externalVehicleStates.push(s.key);
    });

    await settingsApiService.updateConstants([
      { key: EConstantType.VEHICLE_TYPES, value: vehicleStates },
      { key: EConstantType.VEHICLE_TYPES_EXTERNAL_USERS, value: externalVehicleStates },
      { key: EConstantType.VEHICLE_STATE_USAGE_TYPE, value: vehicleUsageTypes },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Fahrzeugarten gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  $scope.updateOnlineDescription = async (): Promise<void> => {
    beginApiTransmission();

    await settingsApiService.updateConstants([
      { key: EConstantType.ONLINE_DESCRIPTION_FORMATTED, value: $scope.constants[EConstantType.ONLINE_DESCRIPTION_FORMATTED] },
      { key: EConstantType.ONLINE_DESCRIPTION_RAW, value: $scope.constants[EConstantType.ONLINE_DESCRIPTION_RAW] },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Beschreibung gespeichert'))
      .catch((e) => logService.error(e));

    endApiTransmission();
  };

  $scope.updateFiletypes = async (): Promise<void> => {
    beginApiTransmission();

    await settingsApiService.updateConstants([
      { key: EConstantType.FILETYPES, value: $scope.filetypes },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Dokumententypen gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  $scope.updateLeadRecipients = async (): Promise<void> => {
    const hasDuplicates = $scope.leadRecipients.some(
      // eslint-disable-next-line max-len
      (recipient, index, array) => array.findIndex((r) => r.subsidiaryId === recipient.subsidiaryId) !== index,
    );

    if (hasDuplicates) {
      toastService.toast(ToastType.ERROR, 'Es dürfen keine doppelten Einträge für den gleichen Betrieb existieren.');
      return;
    }

    beginApiTransmission();

    const leadRecipients: { subsidiaryId: string, users: number[] }[] = [];
    $scope.leadRecipients.forEach((r) => {
      leadRecipients.push({
        subsidiaryId: r.subsidiaryId,
        users: String(r.users).split(',').map((u) => parseInt(u, 10)),
      });
    });

    await settingsApiService.updateConstants([
      { key: EConstantType.LEAD_RECEIPENTS, value: leadRecipients },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Lead-Empfänger gespeichert'))
      .catch((e) => logService.error(e));

    endApiTransmission();
  };

  /**
   * Update the constants for the financing example.
   * Convert values to correct float values first if needed.
   */
  $scope.updateFincancingData = async (): Promise<void> => {
    beginApiTransmission();

    await settingsApiService.updateConstants([
      {
        key: EConstantType.FINANCING_EXAMPLE_PERCENTAGE_UPFRONT,
        value: parseInt($scope.financingExample.upfrontPercentage, 10),
      }, {
        key: EConstantType.FINANCING_EXAMPLE_PERCENTAGE_LAST_PAYMENT,
        value: parseInt($scope.financingExample.lastPaymentPercentage, 10),
      }, {
        key: EConstantType.FINANCING_EXAMPLE_MONTHS,
        value: parseInt($scope.financingExample.numberMonth, 10),
      }, {
        key: EConstantType.FINANCING_EXAMPLE_INTEREST_RATE,
        value: numberConversionService.parseStringFloat($scope.financingExample.interestRate),
      },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Finanzierungsbeispiel gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  $scope.updateMisc = async (): Promise<void> => {
    beginApiTransmission();

    await settingsApiService.updateConstants([
      {
        key: EConstantType.SURVEYS_ENABLED,
        value: $scope.constants[EConstantType.SURVEYS_ENABLED],
      }, {
        key: EConstantType.AVAILABILITY_NOTIFICATIONS,
        // eslint-disable-next-line max-len
        value: $scope.constants[EConstantType.AVAILABILITY_NOTIFICATIONS] ? ($scope.constants[EConstantType.AVAILABILITY_NOTIFICATIONS] as unknown as string).split(', ').map((u) => parseInt(u, 10)) : [],
      }, {
        key: EConstantType.SURVEYS_SENDNIG_DAYS,
        value: parseInt(String($scope.constants[EConstantType.SURVEYS_SENDNIG_DAYS]), 10),
      }, {
        key: EConstantType.DATA_CHECK_MANDATORY,
        value: $scope.constants[EConstantType.DATA_CHECK_MANDATORY],
      }, {
        key: EConstantType.EDIT_SELLER,
        value: $scope.constants[EConstantType.EDIT_SELLER],
      }, {
        key: EConstantType.ONLINE_ADS_WARRANTY_DEFAULT,
        value: !!$scope.constants[EConstantType.ONLINE_ADS_WARRANTY_DEFAULT],
      }, {
        key: EConstantType.MAXIMUM_RESERVATION_DAYS,
        value: parseInt(String($scope.constants[EConstantType.MAXIMUM_RESERVATION_DAYS]), 10),
      }, {
        key: EConstantType.LEAD_ESCALATION_HOURS,
        value: parseInt(String($scope.constants[EConstantType.LEAD_ESCALATION_HOURS]), 10),
      },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Einstellungen gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  /**
   * Update the locations for use in the sales table.
   * Convert the array back to an object where the key is
   * the short form of the location and the value is the location itself.
   */
  $scope.updateLocations = async (): Promise<void> => {
    beginApiTransmission();
    const locations: { [key: string]: string } = {};
    const subsidiaryAssignments: { subsidiaryId: string, location: string }[] = [];

    $scope.locations.forEach((l) => {
      const locationId = `${l.subsidiary}-${l.key}`;
      locations[locationId] = l.value;
      subsidiaryAssignments.push({ location: locationId, subsidiaryId: l.subsidiary });
    });

    await settingsApiService.updateConstants([
      { key: EConstantType.LOCATIONS, value: locations },
      { key: EConstantType.LOCATION_SUBSIDIARY_ASSIGNMENT, value: subsidiaryAssignments },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Lagerorte gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  $scope.updateBankInformation = async (): Promise<void> => {
    beginApiTransmission();
    const banks: { [key: string]: string } = {};

    $scope.bankInformation.forEach((b) => {
      banks[b.value] = b.key;
    });

    await settingsApiService.updateConstants([
      { key: EConstantType.BANK_INFORMATION, value: banks },
    ])
      .then(() => toastService.toast(ToastType.SUCCESS, 'Bankdaten gespeichert'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  /** Updates the transfer costs defined in the view. */
  $scope.updateTransferCosts = async (): Promise<void> => {
    beginApiTransmission();
    const transferCosts: TransferCosts[] = [];

    $scope.transferCosts.forEach((t) => {
      transferCosts.push({
        id: t.id,
        transfer_cost: numberConversionService.parseStringFloat(t.transfer_cost),
        transfer_make: t.transfer_make,
        transfer_model: t.transfer_model,
      });
    });

    await settingsApiService.updateTransferCosts(transferCosts)
      .then(() => toastService.toast(ToastType.SUCCESS, 'Überführungskosten gespeichert.'))
      .catch((e) => logService.error(e));
    endApiTransmission();
  };

  /** Add an element to an array. */
  $scope.addElementToArray = (array: unknown[] | string, elem: unknown): void => {
    // eslint-disable-next-line no-param-reassign
    if (array === '') array = [];
    (array as unknown[]).push(elem);
  };

  /** Removes an element to from an array. */
  $scope.removeObjectFromArray = (index: number, array: unknown[]): void => {
    array.splice(index, 1);
  };

  /** Move an Object in an array. */
  $scope.moveObjectInArray = (array: unknown[], index: number, newPostition: number): void => {
    const tempObject = array.splice(index, 1)[0];
    array.splice(newPostition, 0, tempObject);
  };

  const init = () => {
    prepareConstantsForView();
  };

  init();
}
